// Per-symbol deep dive: quarterly insider statistics (Recharts) from the live
// /statistics endpoint + the most recent Form-4 lines from /search. Both are
// cached server-side in Redis and client-side by React Query.

import { useMemo } from 'react';

import { ExternalLink, X } from 'lucide-react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { useInsiderSearch, useInsiderStatistics } from '../api/insiderTradingApi';
import {
    cn,
    formatCompact,
    formatCompactUsd,
    formatDate,
    formatPrice,
    stockInfoRoute,
} from '../utils/formatters';

interface InsiderSymbolPanelProps {
    symbol: string;
    onClose: () => void;
}

export const InsiderSymbolPanel = ({ symbol, onClose }: InsiderSymbolPanelProps) => {
    const stats = useInsiderStatistics(symbol);
    const trades = useInsiderSearch(symbol);

    const chartData = useMemo(
        () =>
            [...(stats.data?.items ?? [])]
                .slice(0, 12)
                .reverse()
                .map((s) => ({
                    label: `Q${s.quarter} ${String(s.year).slice(2)}`,
                    purchases: s.total_purchases ?? 0,
                    sales: s.total_sales ?? 0,
                    ratio: s.acquired_disposed_ratio,
                })),
        [stats.data],
    );

    const openMarket = useMemo(
        () =>
            (trades.data?.items ?? []).filter(
                (t) => t.transaction_type === 'P-Purchase' || t.transaction_type === 'S-Sale',
            ),
        [trades.data],
    );

    return (
        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/10 flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-black tracking-tight text-indigo-700 dark:text-indigo-300">
                        {symbol} · Insider Statistics
                    </h3>
                    <a
                        href={stockInfoRoute(symbol)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        Open stock deep-dive →
                    </a>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1.5 rounded-md text-slate-500 hover:bg-white dark:hover:bg-slate-800"
                    aria-label="Close panel"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 p-5">
                <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Open-Market Purchases vs Sales per Quarter
                    </h4>
                    {stats.isLoading ? (
                        <div className="h-[280px] rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
                    ) : stats.isError ? (
                        <div className="h-[280px] flex items-center justify-center text-sm text-rose-500">
                            Failed to load statistics.
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="h-[280px] flex items-center justify-center text-sm text-slate-400">
                            No statistics available.
                        </div>
                    ) : (
                        <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -12 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.25} />
                                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                    <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 10 }} />
                                    <YAxis yAxisId="ratio" orientation="right" hide />
                                    <Tooltip
                                        contentStyle={{ fontSize: 12, borderRadius: 8 }}
                                        formatter={(value: number, name: string) =>
                                            name === 'Acquired/Disposed'
                                                ? [value?.toFixed(2) ?? '—', name]
                                                : [value, name]
                                        }
                                    />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar yAxisId="count" dataKey="purchases" name="Purchases" fill="#10b981" radius={[3, 3, 0, 0]} />
                                    <Bar yAxisId="count" dataKey="sales" name="Sales" fill="#f43f5e" radius={[3, 3, 0, 0]} />
                                    <Line
                                        yAxisId="ratio"
                                        type="monotone"
                                        dataKey="ratio"
                                        name="Acquired/Disposed"
                                        stroke="#6366f1"
                                        strokeWidth={2}
                                        dot={false}
                                        connectNulls
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Recent Open-Market Filings
                    </h4>
                    <div className="max-h-[280px] overflow-y-auto rounded-lg border border-slate-100 dark:border-slate-800">
                        {trades.isLoading ? (
                            <div className="h-[280px] bg-slate-100 dark:bg-slate-800 animate-pulse" />
                        ) : openMarket.length === 0 ? (
                            <div className="h-[120px] flex items-center justify-center text-sm text-slate-400">
                                No recent open-market purchases or sales.
                            </div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-[10px] uppercase text-slate-500">
                                    <tr>
                                        <th className="text-left px-2 py-1.5">Filing</th>
                                        <th className="text-left px-2 py-1.5">Insider</th>
                                        <th className="text-left px-2 py-1.5">Type</th>
                                        <th className="text-right px-2 py-1.5">Shares</th>
                                        <th className="text-right px-2 py-1.5">Price</th>
                                        <th className="text-right px-2 py-1.5">Value</th>
                                        <th className="px-2 py-1.5" />
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {openMarket.map((t, i) => {
                                        const isBuy = t.transaction_type === 'P-Purchase';
                                        return (
                                            <tr key={`${t.url ?? ''}-${i}`}>
                                                <td className="px-2 py-1.5 font-mono">{formatDate(t.filing_date)}</td>
                                                <td className="px-2 py-1.5 max-w-[160px] truncate" title={t.reporting_name ?? ''}>
                                                    {t.reporting_name ?? '—'}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    <span
                                                        className={cn(
                                                            'px-1.5 py-0.5 rounded text-[10px] font-bold',
                                                            isBuy
                                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                                                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
                                                        )}
                                                    >
                                                        {isBuy ? 'BUY' : 'SELL'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1.5 text-right font-mono">
                                                    {formatCompact(t.securities_transacted)}
                                                </td>
                                                <td className="px-2 py-1.5 text-right font-mono">{formatPrice(t.price)}</td>
                                                <td className="px-2 py-1.5 text-right font-mono font-semibold">
                                                    {formatCompactUsd(t.transaction_value)}
                                                </td>
                                                <td className="px-2 py-1.5">
                                                    {t.url && (
                                                        <a
                                                            href={t.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 dark:text-blue-400"
                                                            aria-label="Open SEC filing"
                                                        >
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InsiderSymbolPanel;
